import { BaseStoreFunctions } from "@/store/base-functions/base-store-functions"
import { BaseModel } from "@/models/base.model"
import { type IBase } from "@/interfaces/api/base.interface"
import { BaseRepository } from "@/repositories/base-repository"
import { BaseModelController } from "@/controllers/model-controllers/abstract/base-model-controller"
import { type IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"
import { BaseAPIFunctions } from "@/api/base-api-functions"
import { ModelFactory } from "@/factories/model-factory"
import { cloneDeep } from "lodash-es"
import { toRaw } from "vue"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import { HTTPQuery } from "@/builder/http-query"
import { HTTPRequestBuilder } from "@/builder/HTTPRequestBuilder"

export abstract class ModelController<
  Model extends BaseModel,
  IData extends IBase,
  Repository extends BaseRepository<IData>
> extends BaseModelController {
  protected storeFunctions: BaseStoreFunctions<Model, IData>
  protected apiFunctions: BaseAPIFunctions<Model, IData, Repository>

  protected constructor(
    apiFunctions: BaseAPIFunctions<Model, IData, Repository>,
    storeFunctions: BaseStoreFunctions<Model, IData>
  ) {
    super()
    this.apiFunctions = apiFunctions
    this.storeFunctions = storeFunctions

    // Bind methods to the instance, so they can be passed as callbacks (e.g. to useEntityUpdater).
    this.detail = this.detail.bind(this)
    this.get = this.get.bind(this)
    this.getList = this.getList.bind(this)
    this.create = this.create.bind(this)
    this.update = this.update.bind(this)
    this.delete = this.delete.bind(this)
  }

  public getStoreFunctions() {
    return this.storeFunctions
  }

  public async detail(
    id: string,
    requestConfig: IRepositoryRequestConfig = {}
  ): Promise<Model | null> {
    const data = await this.apiFunctions.getModel(id, requestConfig)
    if (data) {
      return await this.storeFunctions.createModel(data)
    }
    return null
  }

  /**
   * @Deprecated
   * @param id
   * @param requestConfig
   * TODO: replace this with detail().
   */
  public async get(
    id: string,
    requestConfig: IRepositoryRequestConfig = {}
  ): Promise<Model | undefined> {
    const idQuery = new ODataQueryBuilder().eq("id", id).build()

    // Merge the id-filter into any existing query without re-encoding or stray "?"/"&" glue.
    const existing = new HTTPQuery(HTTPRequestBuilder.toQueryString(requestConfig.query))
    requestConfig.query = existing.merge(idQuery)

    const result = await this.getList(requestConfig)
    if (result.length > 1) {
      throw new Error(`Query for id ${id} returned multiple results`)
    }
    return result[0]
  }

  public async getList(requestConfig?: IRepositoryRequestConfig): Promise<Model[]> {
    const data = await this.apiFunctions.getModels(requestConfig)
    return (await this.storeFunctions.createModel(data)) as Model[]
  }

  public async create(
    instance: Model,
    requestConfig?: IRepositoryRequestConfig,
    override = false
  ): Promise<Model | undefined> {
    const createdModelData = await this.apiFunctions.createModel(
      instance as unknown as IData,
      requestConfig
    )

    // Note that in some cases, the API might return different data than the model (e.g. exception information).
    // Below we check if the model was created (by reading isSaved) and update the instance with the new data.
    if (createdModelData.isSaved) {
      instance.isSaved = true

      // Copy base properties
      instance.createdAt = createdModelData.createdAt
      instance.updatedAt = createdModelData.updatedAt

      return await this.storeFunctions.createModel(override ? createdModelData : instance)
    }

    return undefined
  }

  public async bulkCreate(
    instances: Model[],
    requestConfig?: IRepositoryRequestConfig,
    override = false
  ): Promise<Model[] | undefined> {
    const createdModelData = await this.apiFunctions.createModel(
      instances as unknown as IData[],
      requestConfig
    )

    if (Array.isArray(createdModelData) && createdModelData.length > 0) {
      const createdModels: (Model | IData)[] = []

      for (let i = 0; i < createdModelData.length; i++) {
        const createdData = createdModelData[i]
        const instance = instances[i]

        instance.isSaved = true

        // Copy base properties
        instance.createdAt = createdData.createdAt
        instance.updatedAt = createdData.updatedAt

        // Collect, but don’t mutate reactivity yet
        createdModels.push(override ? createdData : instance)
      }

      // 🔥 Single reactive batch insert
      return (await this.storeFunctions.createModel(createdModels)) as Model[]
    }
  }

  public async update(
    instance: Model,
    requestConfig?: IRepositoryRequestConfig,
    override: boolean | (() => boolean) = false
  ): Promise<Model | undefined> {
    const updatedModelData = await this.apiFunctions.updateModel(
      instance as unknown as IData,
      requestConfig
    )

    if (updatedModelData.isSaved) {
      // Copy base properties
      instance.updatedAt = updatedModelData.updatedAt

      if (typeof override === "function") {
        override = override()
      }
      if (override) {
        return this.storeFunctions.updateModel(updatedModelData)
      }

      // Return a new (unmanaged) model instance with the updated data.
      // Note that this is a copy!
      return ModelFactory.createModel(this.storeFunctions.getModelType(), updatedModelData, false)
    }

    return undefined
  }

  public async bulkUpdate(
    instances: Model[],
    requestConfig?: IRepositoryRequestConfig,
    override = false
  ): Promise<Model[] | undefined> {
    const updatedModelData = await this.apiFunctions.updateModel(
      instances as unknown as IData[],
      requestConfig
    )

    if (Array.isArray(updatedModelData) && updatedModelData.length > 0) {
      const updatedModels: Model[] = []
      for (let i = 0; i < updatedModelData.length; i++) {
        const updatedData = updatedModelData[i]
        const instance = instances[i]

        // Copy base properties
        instance.updatedAt = updatedData.updatedAt

        if (override) {
          updatedModels.push(this.storeFunctions.updateModel(updatedData)!)
        } else {
          // Return a new (unmanaged) model instance with the updated data.
          // Note that this is a copy!
          updatedModels.push(
            ModelFactory.createModel(this.storeFunctions.getModelType(), updatedData, false)
          )
        }
      }
      return updatedModels
    }
  }

  public async delete(
    instance: Model,
    requestConfig?: IRepositoryRequestConfig,
    waitForResponse = true
  ): Promise<boolean> {
    if (!instance.isSaved) {
      // Local deletion only.
      this.storeFunctions.deleteModel(instance)
      return true
    } else if (!waitForResponse) {
      this.storeFunctions.deleteModel(instance)
    }

    const isDeleted = await this.apiFunctions.deleteModel(instance, requestConfig)
    if (isDeleted) {
      this.storeFunctions.deleteModel(instance)
    }
    return isDeleted
  }

  public async bulkDelete(
    instances: Model[],
    requestConfig?: IRepositoryRequestConfig,
    waitForResponse = true
  ): Promise<boolean> {
    const hasSavedInstances = instances.filter((i) => i.isSaved).length > 0

    if (!hasSavedInstances) {
      for (const instance of instances) {
        this.storeFunctions.deleteModel(instance)
      }
      return true
    } else if (!waitForResponse) {
      for (const instance of instances) {
        this.storeFunctions.deleteModel(instance)
      }
    }

    const isDeleted = await this.apiFunctions.deleteModel(instances, requestConfig)
    if (isDeleted) {
      for (const instance of instances) {
        this.storeFunctions.deleteModel(instance)
      }
    }
    return isDeleted
  }

  public async deleteOrRollback(
    instance: Model,
    requestConfig?: IRepositoryRequestConfig
  ): Promise<boolean> {
    const copy = cloneDeep(instance)
    const isDeleted = await this.delete(instance, requestConfig, false)

    if (!isDeleted) {
      await this.storeFunctions.createModel(copy)
    }
    return isDeleted
  }

  /**
   * Adds the instance to the store before the request, so the UI shows it at once, and removes it
   * again when the request fails. Resolves to the managed instance, or undefined when it was not created.
   */
  public async createOrRollback(
    instance: Model,
    requestConfig?: IRepositoryRequestConfig
  ): Promise<Model | undefined> {
    const managed = await this.storeFunctions.createModel(instance)
    try {
      const created = await this.create(managed, requestConfig)
      if (!created) {
        this.storeFunctions.deleteModel(managed)
        return undefined
      }
      return managed // The instance that is in the store; identical to `created` when managed.
    } catch (error) {
      this.storeFunctions.deleteModel(managed)
      throw error
    }
  }

  /**
   * Sends an instance whose changes are already applied (so the UI updated at once) and restores
   * `originalInstance` (a copy taken before the changes) when the request fails. The server
   * response is merged into the instance (see update() with override).
   */
  public async updateOrRollback(
    instance: Model,
    originalInstance: Model,
    requestConfig?: IRepositoryRequestConfig
  ): Promise<Model | undefined> {
    const restore = () => {
      const restored = this.storeFunctions.updateModel(originalInstance)
      if (toRaw(restored) !== toRaw(instance)) {
        Object.assign(instance, restored) // Unmanaged store functions hand back a copy.
      }
    }

    try {
      const updated = await this.update(instance, requestConfig, true)
      if (!updated) {
        restore()
        return undefined
      }
      return instance
    } catch (error) {
      restore()
      throw error
    }
  }

  public initiateSocketSynchronizationHandlers() {
    // TODO: Implement at some point. Currently auto-initiated inside useSocketSynchronizationHandlers().
    // TODO: could consider pausing as well. For example, to avoid receiving updates when no columns that support
    // TODO: synchronization are visible.
    this.socketSynchronizationHandlers.initiate()
  }
}
